// Two-step verification UI (PB-17-08, PD-020): sign-in step, enrolment with QR code, recovery codes.
import type { MfaEnrolStartResponse } from '@passage/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useMemo, useState, type FormEvent } from 'react';
import { ApiRequestError, api } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { ErrorSummary, Field, Loading } from './ui.tsx';

/**
 * QR modules as one SVG path (OR-09, PD-434). Built from the module matrix and rendered by React as
 * elements and attributes, so no markup string is ever injected into the page: whatever the encoded
 * text contains (a visitor-controlled reference, a URL) can only change which squares are dark.
 */
export function qrModules(text: string, margin = 1): { size: number; d: string } {
  const { modules } = QRCode.create(text, { errorCorrectionLevel: 'M' });
  let d = '';
  for (let y = 0; y < modules.size; y++) for (let x = 0; x < modules.size; x++) if (modules.get(y, x)) d += `M${x + margin} ${y + margin}h1v1h-1z`;
  return { size: modules.size + margin * 2, d };
}

export function QrCode({ text, label }: { text: string; label: string }) {
  const qr = useMemo(() => {
    try {
      return qrModules(text);
    } catch {
      return null; // text too long to encode: show nothing rather than a broken code
    }
  }, [text]);
  if (!qr) return <Loading />;
  return (
    <div className="qr" role="img" aria-label={label}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${qr.size} ${qr.size}`} shapeRendering="crispEdges" aria-hidden="true" focusable="false">
        <rect width={qr.size} height={qr.size} fill="#ffffff" />
        <path d={qr.d} fill="#000000" />
      </svg>
    </div>
  );
}

export function RecoveryCodes({ codes, onDone }: { codes: readonly string[]; onDone: () => void }) {
  const { t } = useI18n();
  return (
    <div>
      <h2>{t('mfa.codes.title')}</h2>
      <p className="note warning" style={{ marginBlockStart: 12 }}>
        {t('mfa.codes.body')}
      </p>
      <ol className="recovery-codes" dir="ltr">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <button className="button primary" type="button" onClick={onDone}>
        {t('mfa.codes.done')}
      </button>
    </div>
  );
}

/** Second step of sign-in. `base` is '/api/v1' or '/api/platform/v1'. */
export function MfaVerifyStep<S>({ base, challengeToken, onDone }: { base: string; challengeToken: string; onDone: (session: S) => void }) {
  const { t } = useI18n();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const m = useMutation({
    mutationFn: () => api<S>('POST', `${base}/auth/mfa`, useRecovery ? { challengeToken, recoveryCode: value } : { challengeToken, code: value.trim() }),
    onSuccess: onDone,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <form onSubmit={submit} noValidate>
      <p className="lede">{t('mfa.verify.lede')}</p>
      <ErrorSummary error={m.error} fieldIds={{}} />
      <Field label={useRecovery ? t('mfa.verify.recovery') : t('mfa.verify.code')}>
        {(p) => (
          <input
            {...p}
            dir="ltr"
            autoComplete="one-time-code"
            inputMode={useRecovery ? 'text' : 'numeric'}
            maxLength={useRecovery ? 20 : 6}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        )}
      </Field>
      <button className="button dark" type="submit" disabled={m.isPending || !value}>
        {m.isPending ? t('common.applying') : t('mfa.verify.submit')}
      </button>
      <button
        className="button quiet"
        type="button"
        onClick={() => {
          setUseRecovery((v) => !v);
          setValue('');
        }}
      >
        {useRecovery ? t('mfa.verify.useCode') : t('mfa.verify.useRecovery')}
      </button>
    </form>
  );
}

/**
 * Enrolment. With `challengeToken` it is the policy-required step of sign-in (returns a session plus
 * recovery codes); without it, the signed-in user turns MFA on from account security.
 */
export function MfaEnrol<R extends { recoveryCodes: readonly string[] }>({ base, challengeToken, required, onDone }: { base: string; challengeToken?: string; required: boolean; onDone: (result: R) => void }) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const start = useQuery({
    queryKey: ['mfa-enrol', base, challengeToken ?? 'self'],
    queryFn: () => api<MfaEnrolStartResponse>('POST', challengeToken ? `${base}/auth/mfa/enrol/start` : `${base}/me/mfa/start`, challengeToken ? { challengeToken } : undefined),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const m = useMutation({
    mutationFn: () => api<R>('POST', challengeToken ? `${base}/auth/mfa/enrol/confirm` : `${base}/me/mfa/confirm`, challengeToken ? { challengeToken, code: code.trim() } : { code: code.trim() }),
    onSuccess: onDone,
  });
  if (start.isPending) return <Loading />;
  if (start.isError) return <ErrorSummary error={start.error} fieldIds={{}} />;
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <p className="lede">{required ? t('mfa.enrol.lede') : t('mfa.enrol.ledeOptional')}</p>
      <ErrorSummary error={m.error} fieldIds={{ code: 'mfa-code' }} />
      <QrCode text={start.data.otpauthUri} label={t('mfa.enrol.title')} />
      <details className="tiny" style={{ marginBlock: 8 }}>
        <summary>{t('mfa.enrol.manual')}</summary>
        <code dir="ltr" className="secret">
          {start.data.secret.replace(/(.{4})/g, '$1 ').trim()}
        </code>
      </details>
      <Field id="mfa-code" label={t('mfa.verify.code')} error={fe.code}>
        {(p) => <input {...p} dir="ltr" autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />}
      </Field>
      <button className="button dark" type="submit" disabled={m.isPending || code.length !== 6}>
        {m.isPending ? t('common.applying') : t('mfa.enrol.submit')}
      </button>
    </form>
  );
}
